import { useContext, useMemo, useState } from "react";
import { Context } from "../../App";
import {
    Box, Typography, TextField, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, IconButton, Button,
    Backdrop, CircularProgress
} from "@mui/material";
import { DeleteForever } from "@mui/icons-material";

export default function ImportBillets({ events, selectedEvent, csvData, updateBillets, close }) {
    const { setIsConnected } = useContext(Context);
    const user = JSON.parse(sessionStorage.getItem('user'));
    const [eventId, setEventId] = useState(selectedEvent || '');
    const [rows, setRows] = useState(csvData || []);
    const [load, setLoad] = useState(false);

    const event = useMemo(() => events.find(e => e._id === eventId), [eventId, events]);
    const tarifs = event?.tarifs || [];

    function deleteRow(index) {
        setRows(old => old.filter((_, i) => i !== index));
    }

    function updateRow(index, field, value) {
        setRows(old => old.map((row, i) => i === index ? { ...row, [field]: value } : row));
    }

    function updateTarif(index, tarifNom) {
        const tarif = tarifs.find(t => t.nom === tarifNom);
        setRows(old => old.map((row, i) =>
            i === index ? { ...row, tarif: tarifNom, price: tarif?.montant ?? 0 } : row
        ));
    }

    function handleSubmit(e) {
        e.preventDefault();
        if (!eventId) return alert('Veuillez sélectionner un événement');
        if (rows.length === 0) return alert('Aucun participant à importer');

        const billets = rows.map(row => ({
            owner: { nom: row.nom, prenom: row.prenom },
            event: eventId,
            statut: 'valide',
            tarif: row.tarif || '',
            price: row.price ?? 0,
            by: user?._id || ''
        }));

        setLoad(true);
        fetch(process.env.REACT_APP_API_URL.concat('/adm/billets/create'), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'authorization': 'Bearer ' + sessionStorage.getItem('token')
            },
            body: JSON.stringify(billets)
        }).then(async response => {
            const token = response.headers.get('x-auth-token');
            if (response.status === 401) {
                sessionStorage.clear();
                setIsConnected(false);
            } else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'));
                }
                if (response.status === 201) {
                    const json = await response.json();
                    updateBillets(json);
                    close();
                } else {
                    alert("Erreur lors de la création des billets");
                }
            }
        }).catch(error => {
            console.error("Error importing billets:", error);
        }).finally(() => {
            setLoad(false);
        });
    }

    return (
        <Box component='form' onSubmit={handleSubmit} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h4" align="center" sx={{ mb: 1, color: 'primary.main' }}>Import CSV</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
                {rows.length} participant(s) détecté(s)
            </Typography>

            <TextField
                select label="Événement" value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                size="small" required sx={{ minWidth: '200px', width: '50%' }}
            >
                {events.map((event) => (
                    <MenuItem key={event._id} value={event._id}>{event.titre}</MenuItem>
                ))}
            </TextField>

            <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 40 }}>#</TableCell>
                            <TableCell>Nom</TableCell>
                            <TableCell>Prénom</TableCell>
                            <TableCell>Tarif</TableCell>
                            <TableCell sx={{ width: 40 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={index} hover>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                    <TextField
                                        size="small" value={row.nom} required
                                        onChange={(e) => updateRow(index, 'nom', e.target.value)}
                                        variant="standard"
                                        slotProps={{ input: { style: { textTransform: 'capitalize' } } }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small" value={row.prenom} required
                                        onChange={(e) => updateRow(index, 'prenom', e.target.value)}
                                        variant="standard"
                                        slotProps={{ input: { style: { textTransform: 'capitalize' } } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ minWidth: 140 }}>
                                    <TextField
                                        select size="small" variant="standard"
                                        value={row.tarif || ''}
                                        onChange={(e) => updateTarif(index, e.target.value)}
                                        required displayEmpty
                                        sx={{ minWidth: 120 }}
                                        disabled={!event}
                                    >
                                        {tarifs.map((t) => (
                                            <MenuItem key={t.nom} value={t.nom}>
                                                {t.nom} — {t.montant?.toFixed(2)} €
                                            </MenuItem>
                                        ))}
                                        <MenuItem value="gratuit">
                                            Gratuit
                                        </MenuItem>
                                    </TextField>
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" onClick={() => deleteRow(index)} aria-label="supprimer la ligne">
                                        <DeleteForever fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Button
                variant="contained" type="submit"
                disabled={rows.length === 0 || !eventId || rows.some(r => !r.tarif)}
                sx={{ alignSelf: 'center', mt: 1 }}
            >
                Créer {rows.length} billet(s)
            </Button>

            <Backdrop open={load} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                <CircularProgress />
            </Backdrop>
        </Box>
    );
}
